<template>
  <div class="flex flex-col gap-1">
    <slot
      v-if="showInputField"
      :value="modelValue"
      :set-value="(value: T | null) => emit('update:modelValue', value)"
      :error="error"
      :disabled="disabled"
      :label="label"
      :default-input-binds="{
        modelValue,
        label,
        disabled,
        hint: error,
        isError: !!error,
        'onUpdate:modelValue': (value: T | null) => emit('update:modelValue', value),
        emptyValueTo: 'undefined' as 'undefined',
      }"
    />
    <LabeledText v-else :label="label">
      <span v-if="!isLoadingDefault">{{ formattedDefaultValue ?? defaultValue }}</span>
      <Spinner v-else class="size-4" />
    </LabeledText>
    <CVButton
      v-if="!disabled"
      variant="link"
      class="self-start !pl-0 !pr-0"
      @click.prevent="toggleShowInputField"
    >
      {{
        !showInputField
          ? (overrideText ?? t('override'))
          : (restoreDefaultText ?? t('restoreDefault'))
      }}
    </CVButton>
  </div>
</template>

<script setup lang="ts" generic="T">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LabeledText from './LabeledText.vue';
import CVButton from '@/components/buttons/CVButton.vue';

const { t } = useI18n();

const props = defineProps<{
  modelValue: T | null;
  defaultValue: T | null;
  formattedDefaultValue?: string | number | null;
  initialOverridenValue?: T | null;
  disabled?: boolean;
  label?: string;
  isLoadingDefault?: boolean;
  error?: string;
  overrideText?: string;
  restoreDefaultText?: string;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: T | null): void;
}>();

const showInputField = ref(props.modelValue !== undefined && props.modelValue !== null);

const toggleShowInputField = () => {
  showInputField.value = !showInputField.value;
  if (showInputField.value) {
    emit('update:modelValue', props.initialOverridenValue ?? props.defaultValue);
  } else {
    emit('update:modelValue', null);
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    showInputField.value = newValue !== null;
  },
);
</script>

<i18n lang="json">
{
  "en": {
    "override": "Override",
    "restoreDefault": "Restore default"
  },
  "de": {
    "override": "Überschreiben",
    "restoreDefault": "Standard wiederherstellen"
  },
  "es": {
    "override": "Sobrescribir",
    "restoreDefault": "Restaurar valor predeterminado"
  }
}
</i18n>
